/**
 * BulkCategorizeDialog — confirm + run a bulk AI categorize over the
 * uncategorized backlog in the current view's scope. Shows the number of
 * conversations that would be classified (one AI call each) as an up-front cost
 * warning before spending credits. Capped per run; the toast reports how many
 * remain so the user can run it again.
 */

import { Loader2, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useEmailUncategorizedCount, useBulkCategorize } from '../hooks/useMailbox';

/** Max conversations categorized per run (matches the server cap). */
const BULK_LIMIT = 200;

export interface BulkCategorizeScope {
  accountId?: string;
  folderId?: string;
  folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
}

export interface BulkCategorizeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: BulkCategorizeScope;
}

export function BulkCategorizeDialog({ open, onOpenChange, scope }: BulkCategorizeDialogProps) {
  const { data: count, isLoading } = useEmailUncategorizedCount(scope, open);
  const bulk = useBulkCategorize();

  const run = () => {
    bulk.mutate({ ...scope, limit: BULK_LIMIT }, { onSuccess: () => onOpenChange(false) });
  };

  const nothingToDo = !isLoading && (count ?? 0) === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Categorize uncategorized mail</DialogTitle>
          <DialogDescription>
            Uses AI to sort uncategorized conversations into a category — one AI request per
            conversation, billed to your credits.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 text-sm">
          {isLoading ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Counting…
            </span>
          ) : nothingToDo ? (
            <span className="text-muted-foreground">No uncategorized conversations here.</span>
          ) : (
            <span>
              <span className="font-medium tabular-nums">{(count ?? 0).toLocaleString()}</span>{' '}
              uncategorized conversation{count === 1 ? '' : 's'} in this view
              {(count ?? 0) > BULK_LIMIT && (
                <span className="block text-xs text-muted-foreground">
                  Up to {BULK_LIMIT.toLocaleString()} are done per run — run again for the rest.
                </span>
              )}
            </span>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            className="gap-1"
            disabled={nothingToDo || isLoading || bulk.isPending}
            onClick={run}
          >
            {bulk.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Categorize
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
